import { useCallback, useMemo, useState } from "react";
import { useUnsavedChangesWarning, useWorkflowFields } from "@repo/shared-components";
import { Spinner } from "@repo/ui";
import { useMetadataChanges } from "~/pages/administration/workflows-page/workflows-dialog/views/metadata-view/hooks/use-metadata-changes";
import { useMetadataFields } from "~/pages/administration/workflows-page/workflows-dialog/views/metadata-view/hooks/use-metadata-fields";
import { MetadataFieldEditor } from "~/pages/administration/workflows-page/workflows-dialog/views/metadata-view/metadata-field-editor";
import {
  MetadataFieldList,
  type FieldRow,
} from "~/pages/administration/workflows-page/workflows-dialog/views/metadata-view/metadata-field-list";
import {
  isFieldType,
  type FieldUnion,
} from "~/pages/administration/workflows-page/workflows-dialog/views/metadata-view/utils/field-type-guards";
import type { CreateFieldRequest, Field } from "~/types/api";
import { useWorkflowFieldMutations } from "../../../hooks/apis/use-workflow-field-mutations";
import { useWorkflowCreationContext } from "../../../workflow-creation-context-provider";
import { WorkflowsDialogFooter } from "../workflows-dialog-footer";

type EditProps = {
  isEditMode?: boolean;
  workflowId?: number;
  onClose?: () => void;
};

type Props = EditProps;

export const MetadataView = ({ isEditMode = false, workflowId, onClose }: Props) => {
  const {
    state: { fields: workflowFields },
    updateState: updateWorkflowState,
  } = useWorkflowCreationContext();

  const { fields: apiFields, isLoading: isLoadingFields } = useWorkflowFields(
    workflowId,
    isEditMode
  );
  const { createField, updateField, deleteField, isCreating, isUpdating, isDeleting } =
    useWorkflowFieldMutations(workflowId || 0);

  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [fieldUnsaved, setFieldUnsaved] = useState<{
    hasChanges: boolean;
    onDiscard: () => void;
  }>({ hasChanges: false, onDiscard: () => {} });

  const handleFieldUnsavedChanges = useCallback((hasChanges: boolean, onDiscard: () => void) => {
    setFieldUnsaved({ hasChanges, onDiscard });
  }, []);

  const setSelectedIndexSafe = useCallback((newIndex: number | null) => {
    setSelectedIndex(newIndex);
    setFieldUnsaved({ hasChanges: false, onDiscard: () => {} });
  }, []);

  const {
    localFields,
    originalFields,
    mutatedFieldIds,
    initialFieldsSnapshot,
    addField,
    updateFieldAtIndex,
    deleteFieldAtIndex,
    reorderFields,
    updateMutationTracking,
    resetMutations,
  } = useMetadataFields({ isEditMode, apiFields, workflowFields });

  const changesSummary = useMetadataChanges({
    isEditMode,
    localFields,
    mutatedFieldIds,
    originalFields,
    initialFieldsSnapshot,
  });

  const hasAnyUnsavedChanges = fieldUnsaved.hasChanges || changesSummary.hasChanges;

  const handleDiscard = useCallback(() => {
    fieldUnsaved.onDiscard();
    resetMutations();
  }, [fieldUnsaved, resetMutations]);

  const { showDiscardDialog } = useUnsavedChangesWarning({
    hasUnsavedChanges: hasAnyUnsavedChanges,
    onDiscard: handleDiscard,
  });

  const cancelAction = useCallback(() => {
    if (isEditMode) onClose?.();
    else updateWorkflowState("currentView", "default");
  }, [isEditMode, onClose, updateWorkflowState]);

  const guardedCancel = useCallback(() => {
    showDiscardDialog(cancelAction);
  }, [showDiscardDialog, cancelAction]);

  const sortedData = useMemo(
    () =>
      localFields
        .map((field, index) => ({ originalObj: field, order: field.order || 0, index }))
        .sort((a, b) => a.order - b.order),
    [localFields]
  );

  const selectedField = useMemo(
    () => (selectedIndex !== null ? (sortedData[selectedIndex]?.originalObj ?? null) : null),
    [selectedIndex, sortedData]
  );

  const handleAdd = () => {
    const newIndex = addField();
    setSelectedIndexSafe(newIndex);
  };

  const handleReorder = (newItems: FieldRow[], oldIndex?: number, newIndex?: number) => {
    const doReorder = () => {
      reorderFields(newItems.map((item, index) => ({ ...item.originalObj, order: index })));
      if (oldIndex !== undefined && newIndex !== undefined && selectedIndex === oldIndex) {
        setSelectedIndexSafe(newIndex);
      }
    };

    if (fieldUnsaved.hasChanges) {
      showDiscardDialog(doReorder);
    } else {
      doReorder();
    }
  };

  const handleInputSave = (updatedField: FieldUnion) => {
    if (selectedIndex === null) return;

    const currentField = localFields[selectedIndex];
    if (!currentField) return;

    const fieldToSave: FieldUnion =
      isEditMode && isFieldType(currentField)
        ? ({ ...updatedField, id: currentField.id, workflowId: currentField.workflowId } as Field)
        : updatedField;

    if (isEditMode) updateMutationTracking(fieldToSave);
    updateFieldAtIndex(selectedIndex, fieldToSave);
  };

  const handleDelete = () => {
    if (selectedIndex === null) return;
    const orderedFields = sortedData.map((d) => d.originalObj);
    deleteFieldAtIndex(selectedIndex, orderedFields);
    setSelectedIndexSafe(null);
  };

  const handleSaveGlobal = async () => {
    if (isEditMode) {
      try {
        const { toCreate, toUpdate, toDelete } = changesSummary;
        for (const fieldId of toDelete) await deleteField(fieldId);
        for (const field of toCreate) {
          await createField({
            ...field,
            options: field.options || null,
            order: field.order || 0,
          });
        }
        for (const field of toUpdate) {
          await updateField({
            fieldId: field.id,
            field: {
              ...field,
              options: field.options || null,
              order: field.order || 0,
            },
          });
        }
        resetMutations();
        onClose?.();
      } catch (error) {
        console.error("Failed to save fields:", error);
      }
    } else {
      updateWorkflowState("fields", localFields as CreateFieldRequest[]);
      updateWorkflowState("currentView", "default");
      resetMutations();
    }
  };

  const isPending = isCreating || isUpdating || isDeleting;

  if (isEditMode && isLoadingFields) {
    return (
      <div className="flex items-center justify-center py-8">
        <Spinner />
      </div>
    );
  }

  return (
    <>
      <div className="flex flex-col gap-6">
        <MetadataFieldList
          localFields={localFields}
          selectedIndex={selectedIndex}
          onSelectRow={(index) => {
            if (fieldUnsaved.hasChanges) {
              showDiscardDialog(() => setSelectedIndexSafe(index));
            } else {
              setSelectedIndexSafe(index);
            }
          }}
          onAdd={() => {
            if (fieldUnsaved.hasChanges) {
              showDiscardDialog(handleAdd);
            } else {
              handleAdd();
            }
          }}
          onDelete={handleDelete}
          onReorder={handleReorder}
        />
        <MetadataFieldEditor
          selectedField={selectedField}
          selectedIndex={selectedIndex}
          hasUnsavedChanges={hasAnyUnsavedChanges}
          showDiscardDialog={showDiscardDialog}
          onSave={handleInputSave}
          onHasUnsavedChanges={handleFieldUnsavedChanges}
          existingNames={localFields.filter((f) => f !== selectedField).map((f) => f.name)}
          onClose={cancelAction}
        />
      </div>
      <WorkflowsDialogFooter
        onCancel={guardedCancel}
        loading={isPending}
        disableSave={!changesSummary.hasChanges || isPending}
        onSave={handleSaveGlobal}
      />
    </>
  );
};
